import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import {
  LayoutDashboard, Users, BarChart3, ClipboardCheck,
  Heart, Rocket, FileCode2, Sun, Moon, LogOut,
} from "lucide-react";

const nav = [
  { label: "OVERVIEW", type: "section" },
  { path: "/leader/dashboard", label: "Regional Overview", icon: LayoutDashboard },
  { path: "/leader/team", label: "Team Composition", icon: Users },
  { label: "INSIGHTS", type: "section" },
  { path: "/leader/skills", label: "Skills Landscape", icon: BarChart3 },
  { path: "/leader/validation", label: "Manager Validation", icon: ClipboardCheck },
  { path: "/leader/icare", label: "ICARE & Collaboration", icon: Heart },
  { label: "DEVELOPMENT", type: "section" },
  { path: "/leader/readiness", label: "Development Readiness", icon: Rocket },
  { path: "/leader/assessment", label: "Assessment Insights", icon: FileCode2 },
];

export default function RegionalSidebar() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const user = JSON.parse(localStorage.getItem("regionalUser") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("regionalToken");
    localStorage.removeItem("regionalUser");
    navigate("/leader");
  };

  return (
    <aside className="w-[220px] h-screen flex flex-col shrink-0 border-r overflow-y-auto"
      style={{
        background: "var(--surface-sidebar, #fff)",
        borderColor: "var(--border-default, #e5e7eb)",
      }}
      data-testid="regional-sidebar"
    >
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
            style={{ background: "linear-gradient(135deg, #00AEEF, #0080b3)" }}>
            {user.region?.[0] || "R"}
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
              {user.region || "Region"}
            </p>
            <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Regional Dashboard</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-2 space-y-0.5">
        {nav.map((item, i) =>
          item.type === "section" ? (
            <p key={i} className="text-[10px] font-semibold uppercase tracking-wider px-3 pt-4 pb-1"
              style={{ color: "var(--text-disabled, #9ca3af)" }}>
              {item.label}
            </p>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                  isActive ? "font-semibold" : ""
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? "#00AEEF" : "var(--text-secondary, #4b5563)",
                background: isActive ? "var(--color-accent-bg, rgba(0,174,239,0.08))" : "transparent",
              })}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </NavLink>
          )
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 space-y-2 border-t" style={{ borderColor: "var(--border-default, #e5e7eb)" }}>
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-[13px] transition-colors"
          style={{ color: "var(--text-secondary)" }}
          data-testid="theme-toggle"
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {isDark ? "Light Mode" : "Dark Mode"}
        </button>

        <div className="flex items-center gap-2 px-3 py-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ background: "#00AEEF" }}>
            {user.name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "RL"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>
              {user.name || "Regional Leader"}
            </p>
            <p className="text-[10px] truncate" style={{ color: "var(--text-muted)" }}>
              {user.email || ""}
            </p>
          </div>
          <button onClick={handleLogout} className="shrink-0" data-testid="regional-logout-btn">
            <LogOut className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          </button>
        </div>
      </div>
    </aside>
  );
}
